import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const PRIMARY      = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';

const AuditLogTab = ({ data, onChange }) => {
    const [auditLogs, setAuditLogs] = useState(data || []);

    useEffect(() => { setAuditLogs(data || []); }, [data]);

    const addNewRow = () => {
        const newRow = { JAuditLogID: null, TransDate: new Date().toISOString().split('T')[0], Remarks: '', LoginID: null, UserName: '' };
        const updated = [...auditLogs, newRow];
        setAuditLogs(updated);
        onChange(updated);
    };

    const deleteRow = (index) => {
        if (window.confirm('Delete this audit log entry?')) {
            const updated = auditLogs.filter((_, i) => i !== index);
            setAuditLogs(updated);
            onChange(updated);
        }
    };

    const updateRow = (index, field, value) => {
        const updated = [...auditLogs];
        updated[index][field] = value;
        setAuditLogs(updated);
        onChange(updated);
    };

    const inputCls    = `w-full px-2.5 py-1.5 border border-[#CBD5E1] rounded text-sm font-[inherit] outline-none transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.1)]`;
    const textareaCls = `w-full min-h-[60px] px-2.5 py-2 border border-[#CBD5E1] rounded text-sm font-[inherit] resize-y outline-none transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.1)]`;
    const disabledCls = ' bg-gray-100 cursor-not-allowed text-gray-500';

    return (
        <div className="p-5 bg-white rounded-lg flex flex-col h-full">

            {/* Toolbar */}
            <div className="mb-4 flex-shrink-0">
                <button onClick={addNewRow}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-150 hover:-translate-y-0.5"
                    style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                    <Plus size={15} /> Add Note
                </button>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto border border-[#E2E8F0] rounded-lg">
                <table className="w-full border-collapse text-sm">
                    <thead className="sticky top-0 z-10"
                        style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                        <tr>
                            {['#', 'Date', 'Remarks', 'User', 'Actions'].map(h => (
                                <th key={h} className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-white border-r border-white/20 last:border-r-0">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {auditLogs.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="py-10 text-center text-gray-400 text-sm">
                                    No audit log entries. Click "Add Note" to add remarks.
                                </td>
                            </tr>
                        ) : (
                            auditLogs.map((row, index) => (
                                <tr key={index}
                                    className={`border-b border-gray-100 transition-colors duration-150 hover:bg-[#EFF6FF] ${
                                        row.JAuditLogID ? 'bg-gray-50/50' : 'bg-white'
                                    }`}>
                                    <td className="px-3 py-2.5 w-10 text-center font-bold text-[#2563EB]">{index + 1}</td>
                                    <td className="px-3 py-2.5 align-top min-w-[150px]">
                                        <input type="date" value={row.TransDate || ''}
                                            onChange={e => updateRow(index, 'TransDate', e.target.value)}
                                            disabled={!!row.JAuditLogID}
                                            className={inputCls + (row.JAuditLogID ? disabledCls : '')} />
                                    </td>
                                    <td className="px-3 py-2.5 align-top">
                                        <textarea value={row.Remarks || ''} rows={2}
                                            onChange={e => updateRow(index, 'Remarks', e.target.value)}
                                            placeholder="Enter remarks"
                                            disabled={!!row.JAuditLogID}
                                            className={textareaCls + (row.JAuditLogID ? disabledCls : '')} />
                                    </td>
                                    <td className="px-3 py-2.5 align-top min-w-[120px]">
                                        <span className="font-medium text-gray-700">{row.username || 'Current User'}</span>
                                    </td>
                                    <td className="px-3 py-2.5 align-top w-16 text-center">
                                        {!row.JAuditLogID && (
                                            <button onClick={() => deleteRow(index)} title="Delete"
                                                className="p-1.5 rounded border-0 cursor-pointer bg-transparent transition-all duration-150"
                                                style={{ color: '#DC2626' }}
                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#FEE2E2'; e.currentTarget.style.color = '#B91C1C'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#DC2626'; }}>
                                                <Trash2 size={15} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Note */}
            <div className="mt-4 px-4 py-3 rounded-r-lg text-[13px] text-gray-700 flex-shrink-0" style={{ background: '#EFF6FF', borderLeft: '4px solid #2563EB' }}>
                <strong style={{ color: '#1D4ED8' }}>Note:</strong> Existing audit log entries cannot be modified or deleted.
            </div>
        </div>
    );
};

export default AuditLogTab;